import { useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/auth';
import { SkeletonCard } from '../../components/Skeleton';

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'https://api.profitos.pro';

interface Schedule {
  id: string;
  name: string;
  channelId: string;
  channelName: string;
  frequency: 'daily' | 'weekly' | 'biweekly' | 'monthly';
  dayOfWeek: number | null; // 0-6, null for daily
  timeOfDay: string; // HH:MM format
  timezone: string;
  isActive: boolean;
  nextRun: string | null;
  lastRun: string | null;
  createdAt: string;
}

interface SchedulesResponse {
  success: boolean;
  data?: {
    schedules: Schedule[];
  };
}

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const FREQUENCY_LABELS: Record<string, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  biweekly: 'Every 2 Weeks',
  monthly: 'Monthly',
};

export function ScheduleTab(): ReactNode {
  const { accessToken } = useAuthStore();
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newSchedule, setNewSchedule] = useState({
    name: '',
    channelId: '',
    frequency: 'weekly' as Schedule['frequency'],
    dayOfWeek: 1,
    timeOfDay: '09:00',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['schedules'],
    queryFn: async (): Promise<SchedulesResponse> => {
      const res = await fetch(`${API_URL}/publishing/schedules`, {
        headers: {
          Authorization: `Bearer ${accessToken ?? ''}`,
        },
      });

      if (!res.ok) {
        if (res.status === 404) {
          return { success: true, data: { schedules: [] } };
        }
        throw new Error('Failed to fetch schedules');
      }

      return res.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async (scheduleData: typeof newSchedule) => {
      const res = await fetch(`${API_URL}/publishing/schedules`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken ?? ''}`,
        },
        body: JSON.stringify(scheduleData),
      });

      if (!res.ok) {
        throw new Error('Failed to create schedule');
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      setShowCreateModal(false);
      setNewSchedule({
        name: '',
        channelId: '',
        frequency: 'weekly',
        dayOfWeek: 1,
        timeOfDay: '09:00',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const res = await fetch(`${API_URL}/publishing/schedules/${id}/toggle`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken ?? ''}`,
        },
        body: JSON.stringify({ isActive }),
      });

      if (!res.ok) {
        throw new Error('Failed to update schedule');
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_URL}/publishing/schedules/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken ?? ''}`,
        },
      });

      if (!res.ok) {
        throw new Error('Failed to delete schedule');
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
  });

  const schedules = data?.data?.schedules ?? [];

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
        <p className="text-red-600">Failed to load schedules. Please try again.</p>
      </div>
    );
  }

  const formatSchedule = (schedule: Schedule): string => {
    const time = schedule.timeOfDay;
    if (schedule.frequency === 'daily') {
      return `Daily at ${time}`;
    }
    const day = schedule.dayOfWeek !== null ? DAYS_OF_WEEK[schedule.dayOfWeek] : '';
    if (schedule.frequency === 'weekly') {
      return `Every ${day} at ${time}`;
    }
    if (schedule.frequency === 'biweekly') {
      return `Every other ${day} at ${time}`;
    }
    return `Monthly on ${day} at ${time}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Publishing Schedules</h3>
          <p className="text-sm text-gray-500">Automate your content publishing with schedules</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          + Create Schedule
        </button>
      </div>

      {schedules.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-12 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">No schedules yet</h3>
          <p className="mt-2 text-gray-500">Create a schedule to automate your content publishing.</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="mt-4 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
          >
            Create Schedule
          </button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {schedules.map((schedule) => (
            <div
              key={schedule.id}
              className={`rounded-lg border bg-white p-6 ${
                schedule.isActive ? '' : 'opacity-60'
              }`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-gray-900">{schedule.name}</h4>
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                        schedule.isActive
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {schedule.isActive ? 'Active' : 'Paused'}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">{schedule.channelName}</p>
                </div>
                <button
                  onClick={() => toggleMutation.mutate({
                    id: schedule.id,
                    isActive: !schedule.isActive,
                  })}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                    schedule.isActive ? 'bg-primary-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      schedule.isActive ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-gray-600">{formatSchedule(schedule)}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" />
                  </svg>
                  <span className="text-gray-600">{schedule.timezone}</span>
                </div>
                {schedule.nextRun && (
                  <div className="flex items-center gap-2 text-sm">
                    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-gray-600">
                      Next: {new Date(schedule.nextRun).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>

              <div className="mt-4 flex gap-3 border-t pt-4">
                <button className="text-sm font-medium text-primary-600 hover:text-primary-900">
                  Edit
                </button>
                <button
                  onClick={() => {
                    if (window.confirm('Delete this schedule?')) {
                      deleteMutation.mutate(schedule.id);
                    }
                  }}
                  className="text-sm font-medium text-red-600 hover:text-red-900"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Create Schedule</h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="scheduleName" className="block text-sm font-medium text-gray-700">
                  Schedule Name
                </label>
                <input
                  type="text"
                  id="scheduleName"
                  value={newSchedule.name}
                  onChange={(e) => setNewSchedule({ ...newSchedule, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  placeholder="e.g., Weekly Newsletter"
                />
              </div>
              <div>
                <label htmlFor="frequency" className="block text-sm font-medium text-gray-700">
                  Frequency
                </label>
                <select
                  id="frequency"
                  value={newSchedule.frequency}
                  onChange={(e) => setNewSchedule({
                    ...newSchedule,
                    frequency: e.target.value as Schedule['frequency'],
                  })}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  {Object.entries(FREQUENCY_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              {newSchedule.frequency !== 'daily' && (
                <div>
                  <label htmlFor="dayOfWeek" className="block text-sm font-medium text-gray-700">
                    Day of Week
                  </label>
                  <select
                    id="dayOfWeek"
                    value={newSchedule.dayOfWeek}
                    onChange={(e) => setNewSchedule({
                      ...newSchedule,
                      dayOfWeek: parseInt(e.target.value, 10),
                    })}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    {DAYS_OF_WEEK.map((day, index) => (
                      <option key={day} value={index}>{day}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label htmlFor="timeOfDay" className="block text-sm font-medium text-gray-700">
                  Time of Day
                </label>
                <input
                  type="time"
                  id="timeOfDay"
                  value={newSchedule.timeOfDay}
                  onChange={(e) => setNewSchedule({ ...newSchedule, timeOfDay: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
            </div>
            {createMutation.error && (
              <p className="mt-2 text-sm text-red-600">{createMutation.error.message}</p>
            )}
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => createMutation.mutate(newSchedule)}
                disabled={!newSchedule.name.trim() || createMutation.isPending}
                className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {createMutation.isPending ? 'Creating...' : 'Create Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
